import { useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { Link } from 'react-router-dom';
import {
    ChevronDownIcon,
    ChevronUpIcon,
    ClockIcon,
    DocumentTextIcon,
    EnvelopeIcon,
    PhoneIcon,
    UserIcon
} from '@heroicons/react/24/outline';
import { tiptapDocToPlainText } from '@odo/shared-ui';
import { SEVERITY } from '../../../patient-files/notes/severity';
import type {
    CommunicationEntry,
    CommunicationPatientRef
} from '../../../../services/communications';
import { timeAgo } from '@odo/shared-ui';
import { UserAvatar } from '../../users/UserAvatar';
import { channelDef } from './channels';
import { DIRECTION_LABEL, formatEntryTime } from './communicationFormatting';
import { EntryBody } from './EntryBody';
import { EntryRowMenu } from './EntryRowMenu';

function plainBody(entry: CommunicationEntry): string {
    const plain = entry.bodyDoc ? tiptapDocToPlainText(entry.bodyDoc) : (entry.body ?? '');
    return plain.trim();
}

function firstLineOf(plain: string): string | null {
    const line = plain
        .split('\n')
        .map((l) => l.trim())
        .find(Boolean);
    return line ?? null;
}

/** Deep-links to the patient's Notes tab (see PatientFiles.tsx's `tab` param). */
function patientNoteUrl(patientId: string): string {
    return `/patients/${patientId}?tab=notes`;
}

// Measures real overflow via ref rather than a line-count heuristic.
// `useLayoutEffect` (not `useEffect`) avoids a clamp-then-snap flicker.
function useClampOverflow(
    text: string,
    skip: boolean
): [RefObject<HTMLParagraphElement>, boolean | null] {
    const ref = useRef<HTMLParagraphElement>(null);
    const [overflows, setOverflows] = useState<boolean | null>(null);

    useLayoutEffect(() => {
        if (skip) return;
        const el = ref.current;
        if (!el) return;
        setOverflows(el.scrollHeight - el.clientHeight > 1);
    }, [text, skip]);

    return [ref, overflows];
}

interface EntryFoldCardProps {
    entry: CommunicationEntry;
    practiceId: string | null;
    /** Last in the (day-grouped, newest-first) list - suppresses the trailing spine connector. */
    last: boolean;
    /** Whether the viewer expanded this body past its clamp - only meaningful once it's known to overflow. */
    longExpanded: boolean;
    onToggleLongExpand: () => void;
    onEdit: () => void;
    onAddPatientNote: () => void;
    onDelete: () => void;
    /** The thread's linked patients - decides whether "Add patient note" is reachable. */
    linkedPatients: CommunicationPatientRef[];
}

// One interaction in the timeline: headline leads, channel/direction/
// duration/contact demote to small pills underneath. Not shared-ui's
// SolidStatusPill - it can't carry a channel's icon or soft/tone color pair.
export function EntryFoldCard({
    entry,
    practiceId,
    last,
    longExpanded,
    onToggleLongExpand,
    onEdit,
    onAddPatientNote,
    onDelete,
    linkedPatients
}: EntryFoldCardProps) {
    const def = channelDef(entry.channel);
    const Icon = def.icon;
    const plain = plainBody(entry);
    const headline = entry.title || firstLineOf(plain) || def.label;
    const patientNotes = entry.patientNotes ?? [];

    const [previewRef, overflows] = useClampOverflow(plain, longExpanded);
    const showFullBody = longExpanded || overflows === false;
    const showExpandControl = longExpanded || overflows === true;

    return (
        <div className="flex gap-4">
            <div className="flex flex-col items-center shrink-0 w-10">
                <span
                    className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: def.soft, color: def.tone }}
                >
                    <Icon className="h-4 w-4" />
                </span>
                {!last && <span className="flex-1 w-px bg-gray-200 mt-2" />}
            </div>

            <div className="flex-1 min-w-0 pb-5">
                <div className="bg-white border border-gray-200 rounded-xl shadow-[0px_1px_2px_rgba(0,0,0,0.04)] overflow-hidden">
                    <div className="px-5 pt-4 pb-3 flex items-stretch justify-between gap-6 border-b border-gray-100">
                        <div className="min-w-0">
                            <p className="text-[15px] font-bold text-gray-900 truncate">
                                {headline}
                            </p>
                            <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                                <span
                                    className="inline-flex items-center gap-1 h-6 pl-1.5 pr-2 rounded-full text-[11px] font-semibold"
                                    style={{ background: def.soft, color: def.tone }}
                                >
                                    <Icon className="h-3 w-3" />
                                    {def.label}
                                </span>
                                {entry.direction && (
                                    <span className="inline-flex items-center h-6 px-2 rounded-full bg-gray-100 text-[11px] font-semibold text-gray-600">
                                        {DIRECTION_LABEL[entry.direction]}
                                    </span>
                                )}
                                {entry.durationMinutes != null && (
                                    <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-gray-100 text-[11px] font-semibold text-gray-600">
                                        <ClockIcon className="h-3 w-3 text-gray-400" />
                                        {entry.durationMinutes} min
                                    </span>
                                )}
                                {entry.contactName && (
                                    <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-gray-100 text-[11px] font-semibold text-gray-600">
                                        <UserIcon className="h-3 w-3 text-gray-400" />
                                        {entry.contactName}
                                    </span>
                                )}
                                {entry.contact && (
                                    <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-gray-100 text-[11px] font-semibold text-gray-600">
                                        {entry.channel === 'email' ? (
                                            <EnvelopeIcon className="h-3 w-3 text-gray-400" />
                                        ) : (
                                            <PhoneIcon className="h-3 w-3 text-gray-400" />
                                        )}
                                        {entry.contact}
                                    </span>
                                )}
                            </div>
                        </div>
                        {/* items-stretch on the row (above) makes this side
                            as tall as the left side's headline+pills block;
                            items-center here balances the avatar/text/menu
                            group in the middle of that height instead of
                            hugging the top, so the header reads as one band. */}
                        <div className="flex items-center gap-3 shrink-0">
                            <div className="flex items-center gap-2 shrink-0">
                                <div className="text-right">
                                    <span className="block text-[13px] font-semibold text-gray-700 whitespace-nowrap">
                                        {entry.authorName}
                                    </span>
                                    <span className="block text-[12px] text-gray-400 whitespace-nowrap">
                                        {formatEntryTime(entry.occurredAt)} ·{' '}
                                        {timeAgo(entry.occurredAt)}
                                    </span>
                                </div>
                                <UserAvatar name={entry.authorName} size={32} />
                            </div>
                            <EntryRowMenu
                                onEdit={onEdit}
                                onAddPatientNote={onAddPatientNote}
                                onDelete={onDelete}
                                addPatientNoteDisabled={!practiceId && linkedPatients.length === 0}
                            />
                        </div>
                    </div>

                    <div className="px-5 py-4">
                        {/* Capped to a readable measure - a full-width paragraph on this
                            column would run past 200 characters per line. */}
                        <div className="max-w-[80ch] flex flex-col gap-3.5">
                            {showFullBody ? (
                                <EntryBody entry={entry} practiceId={practiceId} />
                            ) : (
                                <p
                                    ref={previewRef}
                                    className="text-[14px] text-gray-800 leading-[1.65] whitespace-pre-line line-clamp-6"
                                >
                                    {plain}
                                </p>
                            )}
                            {showExpandControl && (
                                <button
                                    type="button"
                                    onClick={onToggleLongExpand}
                                    className="self-start text-[12px] font-semibold text-gray-600 hover:text-gray-900 flex items-center gap-1.5"
                                >
                                    {longExpanded ? (
                                        <>
                                            <ChevronUpIcon className="h-3.5 w-3.5" />
                                            Show less
                                        </>
                                    ) : (
                                        <>
                                            <ChevronDownIcon className="h-3.5 w-3.5" />
                                            Show more
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                        {patientNotes.length > 0 && (
                            <div className="flex items-center gap-1.5 flex-wrap text-xs text-gray-500 mt-3">
                                <DocumentTextIcon className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                                <span>Patient note ·</span>
                                {patientNotes.map((note, index) => (
                                    <span key={note.id} className="inline-flex items-center gap-1">
                                        <span
                                            className={`w-1.5 h-1.5 rounded-full shrink-0 ${SEVERITY[note.severity].dot}`}
                                        />
                                        <Link
                                            to={patientNoteUrl(note.patientId)}
                                            className="hover:underline hover:text-gray-900 cursor-pointer transition-colors"
                                        >
                                            {note.patientName}
                                        </Link>
                                        {index < patientNotes.length - 1 && ','}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
